import _ from 'lodash';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DragDropContext } from 'react-beautiful-dnd';
import { TextField, InputAdornment } from '@material-ui/core';
import SearchFeaturePageShell from 'components/shell/SearchFeaturePageShell';
import { Search } from '@material-ui/icons';
import { useLocalizedMessage } from 'util/useLocalizedMessage';
import { actions, loadFilters, saveFilters } from 'slices/FiltersSlice';
import { actions as facetValuesActions } from 'slices/FacetValuesSlice';
import { useLocalizedNotifications } from 'util/useLocalizedNotifications';
import SaveActionBtnGrp from 'components/kea/settings/SaveActionBtnGrp';
import {
  UnsavedChangesDialog,
  useUnsavedChangesGuard,
} from 'components/kea/UnsavedChangesGuard';
import IndexingPageGuard from 'features/operation-supervisor/IndexingPageGuard';
import { serviceRegistry } from 'store/index';
import CanonicalFacetPolicyPage from 'pluginAdmin/features/facets/CanonicalFacetPolicyPage';
import { useShellAppContext } from 'features/shell/context/useShellAppContext';
import FacetEditModal from './FacetEditModal';
import FacetValueEditorPanel from './FacetValueEditorPanel';
import FiltersBoard from './components/FiltersBoard';
import styles from './Filters.module.scss';

const LegacyFilters = () => {
  const localizeMessage = useLocalizedMessage();
  const notify = useLocalizedNotifications();
  const { capabilities, compatibility } = useShellAppContext();
  const dispatch = useDispatch();
  const { currentShop } = useSelector((state) => state.app);
  const { currentFacets, originalFacets, isFetching, isSaving, searchTerm, hasLoaded } = useSelector((state) => state.filters);
  const loadError = useSelector((state) => state.filters.loadError);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedFacet, setSelectedFacet] = useState(null);
  const advancedEditingEnabled = compatibility?.filters?.advancedEditor
    ?? capabilities?.facetPolicyEditorV1 === true;
  const valueMappingsEnabled = compatibility?.filters?.valueMappings
    ?? (advancedEditingEnabled && capabilities?.facetValueMappingsV1 === true);
  const deferredReindexEnabled = compatibility?.catalogUpdates?.deferred
    ?? capabilities?.deferredReindexV1 === true;

  useEffect(() => {
    dispatch(loadFilters(currentShop));
  }, [dispatch, currentShop]);

  const hasChanges = !_.isEqual(currentFacets, originalFacets);
  const unsavedGuard = useUnsavedChangesGuard(hasChanges);

  const splitZones = useCallback((list) => {
    const head = list.filter((f) => !f.orderNatural && f.order >= 0).sort((a, b) => a.order - b.order);
    const nat = list.filter((f) => f.orderNatural);
    const tail = list.filter((f) => !f.orderNatural && f.order < 0).sort((a, b) => a.order - b.order);
    return { head, nat, tail };
  }, []);

  const reindexFromZones = useCallback(({ head, nat, tail }) => {
    const h = head.map((it, i) => ({ ...it, orderNatural: false, order: i }));
    const n = nat.map((it) => {
      const { order, ...rest } = it;
      return { ...rest, orderNatural: true };
    });
    const tLen = tail.length;
    const t = tail.map((it, i) => ({ ...it, orderNatural: false, order: -(tLen - i) }));
    return [...h, ...n, ...t];
  }, []);

  const zones = useMemo(() => splitZones(currentFacets), [currentFacets, splitZones]);

  const commitCurrentFacets = useCallback((next) => {
    if (_.isEqual(next, currentFacets)) return;
    dispatch(actions.setCurrentFacets(next));
  }, [currentFacets, dispatch]);

  const handleDragEnd = (result) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;

    const srcZone = source.droppableId;
    const dstZone = destination.droppableId;

    if (srcZone === 'nat' && dstZone === 'nat' && source.index !== destination.index) {
      notify.info('notifications.info.cannotReorderNatural');
      return;
    }

    const moved = currentFacets.find((f) => String(f._cid) === String(draggableId));
    if (!moved) return;

    const others = currentFacets.filter((f) => String(f._cid) !== String(draggableId));
    const { head, nat, tail } = splitZones(others);

    const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
    const h = head.slice();
    const n = nat.slice();
    const t = tail.slice();

    if (dstZone === 'head') {
      const idx = clamp(destination.index, 0, h.length);
      h.splice(idx, 0, { ...moved, orderNatural: false, order: 0 });
    } else if (dstZone === 'tail') {
      const idx = clamp(destination.index, 0, t.length);
      t.splice(idx, 0, { ...moved, orderNatural: false, order: -1 });
    } else {
      const idx = clamp(destination.index, 0, n.length);
      n.splice(idx, 0, { ...moved, orderNatural: true });
    }

    const next = reindexFromZones({ head: h, nat: n, tail: t });
    commitCurrentFacets(next);
  };

  const applySimpleUpdate = (cid, changes) => {
    const next = currentFacets.map((f) => (f._cid === cid ? { ...f, ...changes } : f));
    commitCurrentFacets(next);
  };

  const applyPositionalUpdate = (cid, changes) => {
    const target = currentFacets.find((f) => f._cid === cid);
    if (!target) return applySimpleUpdate(cid, changes);

    const updated = { ...target, ...changes };
    const rest = currentFacets.filter((f) => f._cid !== cid);
    const { head, nat, tail } = splitZones(rest);

    const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

    if (updated.orderNatural) {
      const n = nat.slice();
      n.splice(0, 0, { ...updated, orderNatural: true });
      const next = reindexFromZones({ head, nat: n, tail });
      commitCurrentFacets(next);
      return;
    }

    if (typeof updated.order !== 'number') {
      const n = nat.slice();
      n.splice(0, 0, { ...updated, orderNatural: true });
      const next = reindexFromZones({ head, nat: n, tail });
      commitCurrentFacets(next);
      return;
    }

    if (updated.order >= 0) {
      const h = head.slice();
      const idx = clamp(updated.order, 0, h.length);
      h.splice(idx, 0, { ...updated, orderNatural: false, order: 0 });
      const next = reindexFromZones({ head: h, nat, tail });
      commitCurrentFacets(next);
    } else {
      const t = tail.slice();
      const k = Math.abs(updated.order);
      const idx = clamp(t.length - k, 0, t.length);
      t.splice(idx, 0, { ...updated, orderNatural: false, order: -1 });
      const next = reindexFromZones({ head, nat, tail: t });
      commitCurrentFacets(next);
    }
  };

  return (
    <SearchFeaturePageShell
      pageId="customizationFilters"
      description={localizeMessage('customization.filters.description')}
      isLoading={!hasLoaded && isFetching}
      error={loadError && !isFetching ? loadError : null}
      actions={(
        <SaveActionBtnGrp
          isFetching={isFetching || isSaving}
          buttons={[
            {
              text: localizeMessage('common.save'),
              action: () => dispatch(saveFilters({
                shopId: currentShop,
                deferredReindex: deferredReindexEnabled,
              })),
              color: 'primary',
            },
            {
              text: localizeMessage('customization.filters.actions.discard'),
              action: () => dispatch(actions.discardChanges()),
              color: 'secondary',
            },
          ]}
          disabled={!hasChanges}
        />
      )}
    >
      <IndexingPageGuard>
        <div className={styles.page}>
          <div className={styles.toolbar}>
            <TextField
              className={styles.search}
              fullWidth
              size="small"
              variant="outlined"
              placeholder={localizeMessage('customization.filters.placeholder.filterByTitleOrId')}
              value={searchTerm}
              onChange={(e) => dispatch(actions.setSearchTerm(e.target.value))}
              inputProps={{
                'aria-label': localizeMessage('customization.filters.placeholder.filterByTitleOrId'),
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search className={styles.searchIcon} />
                  </InputAdornment>
                ),
              }}
            />
            <span className={styles.countBadge} data-testid="facets-count">
              {localizeMessage('customization.filters.count', { count: currentFacets.length })}
            </span>
          </div>

          <DragDropContext onDragEnd={handleDragEnd}>
            <FiltersBoard
              head={zones.head}
              nat={zones.nat}
              tail={zones.tail}
              searchTerm={searchTerm}
              onEdit={advancedEditingEnabled ? (facet) => {
                setSelectedFacet(facet);
                setEditModalOpen(true);
              } : null}
              onEditValues={valueMappingsEnabled ? (facet) => {
                dispatch(facetValuesActions.openEditor({ facetId: facet.id, facetType: facet.type }));
              } : null}
              onUpdate={(cid, changes) => {
                if ('order' in changes || 'orderNatural' in changes) {
                  applyPositionalUpdate(cid, changes);
                } else {
                  applySimpleUpdate(cid, changes);
                }
              }}
              dndEnabled={!searchTerm}
            />
          </DragDropContext>

          <FacetEditModal
            open={editModalOpen}
            onClose={() => setEditModalOpen(false)}
            facet={selectedFacet}
            onSave={(facetData) => {
              if (selectedFacet) {
                if ('order' in facetData || 'orderNatural' in facetData) {
                  applyPositionalUpdate(selectedFacet._cid, facetData);
                } else {
                  applySimpleUpdate(selectedFacet._cid, facetData);
                }
              } else {
                dispatch(actions.addFacet({
                  ...facetData,
                  id: Date.now().toString(),
                }));
              }
              setEditModalOpen(false);
            }}
          />

          <FacetValueEditorPanel />
          <UnsavedChangesDialog guard={unsavedGuard} />
        </div>
      </IndexingPageGuard>
    </SearchFeaturePageShell>
  );
};

const Filters = () => {
  const translate = useLocalizedMessage();
  const { capabilities, compatibility } = useShellAppContext();
  const facetPolicyApi = serviceRegistry && serviceRegistry.facetPolicyApi;
  const advancedEditingEnabled = compatibility?.filters?.advancedEditor
    ?? capabilities?.facetPolicyEditorV1 === true;
  const deferredReindexEnabled = compatibility?.catalogUpdates?.deferred
    ?? capabilities?.deferredReindexV1 === true;
  if (!facetPolicyApi || !advancedEditingEnabled) return <LegacyFilters />;
  return (
    <CanonicalFacetPolicyPage
      api={facetPolicyApi}
      indexingApi={serviceRegistry.indexingApi}
      deferredReindex={deferredReindexEnabled}
      fallback={<LegacyFilters />}
      translate={translate}
    />
  );
};

export default Filters;
